// alpine/acceptStatusStore.js
// 고객 요청(cart/call) 접수 상태 폴링 스토어.
// 폴링 수명 규칙: 요청 직후에만 시작, 접수 확인 즉시 중단, 타임아웃 자동 중단,
// 탭 비활성 시 일시정지(복귀 시 즉시 1회 조회 후 재개).
export function createAcceptStatusStore({ endpoint } = {}) {
  const POLL_MS = 3000;
  const BACKOFF_MS = 10000; // 연속 네트워크 오류 시 완화 간격
  const BACKOFF_AFTER = 3;
  const TIMEOUT_MS = 5 * 60 * 1000;
  const ACCEPTED_HIDE_MS = 8000;

  const safeJsonParse = (raw, fallback) => {
    try {
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  const safeStorageGet = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const safeStorageSet = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {}
  };

  const safeStorageRemove = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {}
  };

  // requestStore.js 의 getPrefix 와 같은 규칙(매장별 키 분리).
  const getPrefix = () => {
    const direct = window.QRSHOP?.prefix || window.QRSHOP_BASE?.prefix;
    if (direct) return direct;
    const shop = window.QRSHOP?.shop || window.QRSHOP_BASE?.shop || '';
    return shop ? `qrshop:v1:${shop}:` : 'qrshop:v1:global:';
  };

  const pendingKey = () => `${getPrefix()}request:pending`;

  const getEndpoint = () =>
    endpoint ||
    window.frontendAjaxObject?.requestStatusEndpoint ||
    '/wp-json/dcraft/v1/request-status';

  return {
    // 'waiting' | 'accepted' | null(비표시)
    phase: null,
    requestType: '',

    _pending: null, // { commentId, token, type, startedAt }
    _timerId: null,
    _hideTimerId: null,
    _failCount: 0,
    _visibilityBound: false,

    get open() {
      return this.phase !== null;
    },

    // 요청 전송 성공 직후 호출. 새로고침 재개를 위해 sessionStorage에 기록.
    begin({ commentId, token, type } = {}) {
      const id = Number(commentId || 0);
      const safeToken = String(token || '');
      if (!id || !safeToken) return;

      const pending = {
        commentId: id,
        token: safeToken,
        type: type === 'call' ? 'call' : 'cart',
        startedAt: Date.now(),
      };
      safeStorageSet(pendingKey(), JSON.stringify(pending));
      this._start(pending);
    },

    // 페이지 로드 시 호출. 타임아웃 창 안이면 폴링 재개.
    resume() {
      const pending = safeJsonParse(safeStorageGet(pendingKey()), null);
      if (!pending || !Number(pending.commentId) || !pending.token) return;

      if (Date.now() - Number(pending.startedAt || 0) >= TIMEOUT_MS) {
        safeStorageRemove(pendingKey());
        return;
      }
      this._start(pending);
    },

    _start(pending) {
      this._stopTimer();
      if (this._hideTimerId) {
        clearTimeout(this._hideTimerId);
        this._hideTimerId = null;
      }

      this._pending = pending;
      this.requestType = pending.type || '';
      this.phase = 'waiting';
      this._failCount = 0;
      this._bindVisibility();

      this._poll();
      this._schedule();
    },

    _bindVisibility() {
      if (this._visibilityBound) return;
      this._visibilityBound = true;

      document.addEventListener('visibilitychange', () => {
        if (this.phase !== 'waiting') return;

        if (document.hidden) {
          this._stopTimer();
        } else {
          this._poll();
          this._schedule();
        }
      });
    },

    _schedule() {
      this._stopTimer();
      const delay = this._failCount >= BACKOFF_AFTER ? BACKOFF_MS : POLL_MS;

      this._timerId = setTimeout(async () => {
        await this._poll();
        if (this.phase === 'waiting' && !document.hidden) {
          this._schedule();
        }
      }, delay);
    },

    _stopTimer() {
      if (this._timerId) {
        clearTimeout(this._timerId);
        this._timerId = null;
      }
    },

    async _poll() {
      const pending = this._pending;
      if (!pending || this.phase !== 'waiting') return;

      if (Date.now() - Number(pending.startedAt || 0) >= TIMEOUT_MS) {
        // 접수가 늦어질 뿐 실패가 아니므로 에러 표시 없이 조용히 중단.
        this._finishQuietly();
        return;
      }

      let res;
      try {
        const url = `${getEndpoint()}?comment_id=${encodeURIComponent(
          pending.commentId
        )}&token=${encodeURIComponent(pending.token)}`;
        res = await fetch(url, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          credentials: 'same-origin',
        });
      } catch {
        this._failCount += 1;
        return;
      }

      // 404 = 삭제됨/토큰 불일치 → 재시도 무의미, 종료.
      if (res.status === 404) {
        this._finishQuietly();
        return;
      }

      if (!res.ok) {
        this._failCount += 1;
        return;
      }

      this._failCount = 0;
      const body = await res.json().catch(() => null);
      if (body?.accepted === true) {
        this._markAccepted();
      }
    },

    _markAccepted() {
      this._stopTimer();
      safeStorageRemove(pendingKey());
      this._pending = null;
      this.phase = 'accepted';

      try {
        navigator.vibrate?.(200);
      } catch {}

      this._hideTimerId = setTimeout(() => {
        this.phase = null;
        this._hideTimerId = null;
      }, ACCEPTED_HIDE_MS);
    },

    _finishQuietly() {
      this._stopTimer();
      safeStorageRemove(pendingKey());
      this._pending = null;
      this.phase = null;
    },

    // 접수 완료 배너 클릭 시 닫기.
    dismissAccepted() {
      if (this.phase !== 'accepted') return;
      if (this._hideTimerId) {
        clearTimeout(this._hideTimerId);
        this._hideTimerId = null;
      }
      this.phase = null;
    },
  };
}
